import ConfirmationModal from "components/ConfirmationModal";
import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { ChangeEvent, useState } from "react";
import {
  addAppealCouncilLitigation,
  addAppealLitigation,
} from "services/advocateComplaintService";
import { AdvocateComplaints, CourtAppeal, Litigation } from "types";
import { LoadingIcon } from "utils/constant";
import { ADVOCATE_ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: CourtAppeal;
  complaintData: AdvocateComplaints;
  onLitigationRefresh: any;
  onCouncilLitigationRefresh: any;
}

export default function AddCourtDailyHearingModal({
  isOpen,
  setIsOpen,
  summonData,
  complaintData,
  onLitigationRefresh,
  onCouncilLitigationRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const canCreateInspectionDailyHearing = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateCouncilDailyHearing = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const { showToast } = useToast();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formState, setFormState] = useState<Litigation>({
    is_final_verdict_given: false,
    hearing_date: "",
    next_appointment_date: "",
    given_order: "",
    appointment_reason: "verbal litigation",
    daily_report_summary: "",
    summon: null,
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
    setFormState((prev) => ({
      ...prev,
      [name]:
        name === "is_final_verdict_given" || name === "prosecutor_opinion_type"
          ? checked
          : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!summonData) {
      showToast(t("cannotFindSummon"), "error");
      return;
    }

    const data = {
      ...formState,
      is_final_verdict_given:
        formState.appointment_reason === "final decision" ? true : false,
      appeal_to_court: summonData?.id || "",
      admin_unit_id: complaintData?.admin_unit_id || "",
      org_id: complaintData?.org_id || "",
      accused_advocate_name: complaintData?.accused_name || "",
      user_id_accused_advocate: complaintData?.userid_accused_name || "",
      accused_license_number: complaintData?.accused_license_number || "",
    };
    const isFormData = false;

    try {
      const api = canCreateInspectionDailyHearing
        ? addAppealLitigation
        : canCreateCouncilDailyHearing
          ? addAppealCouncilLitigation
          : null;
      if (!api) {
        showToast(t("noPermissionToCreateDailyHearing"), "error");
        return;
      }

      await api(data, isFormData);

      // if (resData) {
      //   await editComplaint({
      //     ...complaintData,
      //     status: COMPLIANT_STATUS.UNDER_LITIGATION,
      //   });
      // }

      onLitigationRefresh();
      onCouncilLitigationRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        is_final_verdict_given: false,
        hearing_date: "",
        next_appointment_date: "",
        appointment_reason: "",
        daily_report_summary: "",
        summon: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);

    const data = {
      ...formState,
      is_final_verdict_given:
        formState.appointment_reason === "final decision" ? true : false,
      appeal_to_court: summonData?.id || "",
      admin_unit_id: complaintData?.admin_unit_id || "",
      org_id: complaintData?.org_id || "",
      accused_advocate_name: complaintData?.accused_name || "",
      user_id_accused_advocate: complaintData?.userid_accused_name || "",
      accused_license_number: complaintData?.accused_license_number || "",
    };
    const isFormData = false;

    try {
      const api = canCreateInspectionDailyHearing
        ? addAppealLitigation
        : canCreateCouncilDailyHearing
          ? addAppealCouncilLitigation
          : null;

      if (!api) {
        showToast(t("noPermissionToCreateDailyHearing"), "error");
        return;
      }

      await api(data, isFormData);

      onLitigationRefresh();
      onCouncilLitigationRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        is_final_verdict_given: false,
        hearing_date: "",
        next_appointment_date: "",
        appointment_reason: "",
        daily_report_summary: "",
        summon: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addCourtAppealDailyHearing")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("hearingDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="hearing_date"
              placeholder={t("datePlaceholder")}
              value={formState.hearing_date}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("nextAppointmentDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="date"
              name="next_appointment_date"
              placeholder={t("datePlaceholder")}
              value={formState.next_appointment_date}
              onChange={handleChange}
              min={
                formState.hearing_date
                  ? new Date(
                      new Date(formState.hearing_date).getTime() + 86400000,
                    )
                      .toISOString()
                      .split("T")[0] // strictly after hearing_date
                  : new Date().toISOString().split("T")[0] // fallback to today
              }
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("givenOrder")}
            </label>
            <textarea
              rows={3}
              name="given_order"
              value={formState.given_order}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("appointmentReason")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="appointment_reason"
              value={formState.appointment_reason ?? ""}
              onChange={handleChange}
            >
              {[
                "verbal litigation",
                "evidence inspection",
                "evidence defence",
                "final decision",
              ]?.map((list) => (
                <option key={list} value={list}>
                  {list}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("dailyReportSummary")}
            </label>
            <textarea
              rows={3}
              name="daily_report_summary"
              value={formState.daily_report_summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>

          {/* <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("reportType")}
            </label>

            <div className="flex items-center gap-2 bg-gray-100 border p-2 rounded-md">
              <input
                className="form-checkbox h-4 w-4 text-blue-600"
                type="checkbox"
                name="is_final_verdict_given"
                checked={formState.is_final_verdict_given}
                onChange={handleChange}
              />
              <span className="text-sm text-gray-700">
                {t("isItFinalDecision")}
              </span>
            </div>
          </div> */}
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("addDailyHearing")
          )}
        </button>
      </form>

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirm}
        isLoading={loading}
      />
    </SideModal>
  );
}
